import { useState } from 'react';
import type { TimelineStep } from '@repancy/core';
import { TimelineStepKind } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface StepCardProps {
  step: TimelineStep;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onFork: () => void;
}

const STEP_ICONS: Record<TimelineStepKind, string> = {
  [TimelineStepKind.ToolUse]: '🔧', // wrench
  [TimelineStepKind.Reasoning]: '💭', // thought bubble
  [TimelineStepKind.TextOutput]: '📝', // memo
};

const cardBaseStyle: React.CSSProperties = {
  borderStyle: 'solid',
  background: '#ece9d8',
  marginBottom: 4,
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative',
};

const contentStyle: React.CSSProperties = {
  fontFamily: "var(--xp-font-mono)",
  fontSize: 11,
  lineHeight: 1.5,
  padding: 8,
  background: '#fff',
  color: '#000',
  borderTop: '1px solid #808080',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 300,
  overflowY: 'auto',
};

const forkBtnStyle: React.CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 10,
  padding: '1px 6px',
  background: '#ece9d8',
  color: '#000',
  borderStyle: 'solid',
  cursor: 'pointer',
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
};

export function StepCard({ step, isActive, isExpanded, onToggle, onFork }: StepCardProps) {
  const [hovered, setHovered] = useState(false);

  const tokenCount = step.endToken - step.startToken;

  return (
    <div
      style={{
        ...cardBaseStyle,
        borderLeftWidth: isActive ? 3 : 2,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={testIds.promptLab.stepCard(step.id)}
    >
      <div style={headerStyle} onClick={onToggle} data-testid={testIds.promptLab.stepToggle(step.id)}>
        <span style={{ fontSize: 14 }}>{STEP_ICONS[step.kind] ?? ''}</span>
        <span style={{ flex: 1, fontWeight: isActive ? 'bold' : 'normal' }}>
          {step.label}
        </span>
        <span style={{ fontSize: 10, color: '#808080' }}>
          {tokenCount > 0 ? `${tokenCount} tok` : ''}
        </span>
        <span style={{ fontSize: 10, color: '#808080' }}>
          {isExpanded ? '▲' : '▼'}
        </span>
        {hovered && (
          <button
            style={forkBtnStyle}
            onClick={(e) => {
              e.stopPropagation();
              onFork();
            }}
            data-testid={testIds.promptLab.stepFork(step.id)}
          >
            Fork Here
          </button>
        )}
      </div>

      {isExpanded && (
        <div style={contentStyle}>
          {step.content || '(empty)'}
        </div>
      )}
    </div>
  );
}
